import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import FlashMessage from './FlashMessage';

// Default duration for flash messages (5 seconds)
export const DEFAULT_DURATION = 5000;

// Create the context
const FlashMessageContext = createContext();

/**
 * Flash Message Provider Component
 * Provides a way to show flash messages from anywhere in the application
 */
export const FlashMessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const nextId = useRef(0);

  // EVERY function handed out below is memoized, and the context value with
  // them. That is not a micro-optimisation -- it is a correctness requirement.
  //
  // Consumers put `showError` in useCallback/useEffect dependency arrays (25
  // sites across 14 files at the time of writing). When these were plain arrow
  // functions they were rebuilt on every provider render, so a component that
  // fetched, failed, and reported the failure spun:
  //
  //     fetch fails -> showError -> provider setState -> NEW showError identity
  //     -> new fetch callback identity -> effect re-runs -> fetch fails -> ...
  //
  // Seen live on 2026-08-27: with DocuWare unreachable, the invoice Documents
  // panel fired hundreds of identical requests per second at our own API and
  // never stopped. ChecklistPanel carries the same shape. Anything added later
  // that depends on these functions inherits the fix rather than the bug.
  const showMessage = useCallback((message, type = 'info', duration = DEFAULT_DURATION) => {
    // A counter, not Date.now(): two messages raised in the same millisecond
    // used to collide, and the id is the React key for the rendered list.
    const id = `flash-${nextId.current++}`;

    setMessages(prevMessages => [
      ...prevMessages,
      { id, message, type, duration }
    ]);

    // Return the message ID in case it's needed for manual removal
    return id;
  }, []);

  // Convenience methods for different message types
  const showSuccess = useCallback(
    (message, duration = DEFAULT_DURATION) => showMessage(message, 'success', duration),
    [showMessage]
  );

  const showError = useCallback(
    (message, duration = DEFAULT_DURATION) => showMessage(message, 'error', duration),
    [showMessage]
  );

  const showInfo = useCallback(
    (message, duration = DEFAULT_DURATION) => showMessage(message, 'info', duration),
    [showMessage]
  );

  // Remove a message by its ID
  const removeMessage = useCallback((id) => {
    setMessages(prevMessages =>
      prevMessages.filter(message => message.id !== id)
    );
  }, []);

  // Memoized too: a fresh object here would re-render every consumer on every
  // message, which is how the identities above would leak back out.
  const value = useMemo(
    () => ({ showMessage, showSuccess, showError, showInfo, removeMessage }),
    [showMessage, showSuccess, showError, showInfo, removeMessage]
  );

  return (
    <FlashMessageContext.Provider value={value}>
      {children}
      
      {/* Render all active messages */}
      {messages.map(({ id, message, type, duration }) => (
        <FlashMessage
          key={id}
          message={message}
          type={type}
          duration={duration}
          onClose={() => removeMessage(id)}
        />
      ))}
    </FlashMessageContext.Provider>
  );
};

// Custom hook to use the flash message context
export const useFlashMessage = () => {
  const context = useContext(FlashMessageContext);
  
  if (!context) {
    throw new Error('useFlashMessage must be used within a FlashMessageProvider');
  }
  
  return context;
};

export default FlashMessageContext; 